import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import DynamicDialog from "../../Modals/CommanDialog";

const initialForm = {
  tagNo: "",
  barcode: "",
  rfid: "",
  store: "",
  vendorName: "",
  purchaseDate: "",
  cost: "",
  quantity: "1",
};

const JewelleryInventory = ({
  open,
  onClose,
  jewellery,
  stores = [],
  resetKey,
  loading = false,
  onAdd,
  onEdit,
  onToggleActive,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [toggleRow, setToggleRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [errorMessages, setErrorMessages] = useState({});

  // reset the form when the dialog closes or after a successful add/edit
  useEffect(() => {
    setFormData(initialForm);
    setErrorMessages({});
    setShowForm(false);
    setEditingId(null);
  }, [open, resetKey]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "quantity" && !/^\d*$/.test(value)) return;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrorMessages({});
  };

  const handleToggleSubmit = () => {
  onToggleActive(toggleRow);
  setToggleRow(null);
};

  const resetForm = () => {
    setFormData(initialForm);
    setErrorMessages({});
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (row) => {
    const purchaseDate = row.purchaseDate
      ? moment(row.purchaseDate).format("YYYY-MM-DD")
      : "";
    const storeId =
      row.store?._id || (typeof row.store === "string" ? row.store : "");

    setEditingId(row._id);
    setFormData({
      tagNo: row.tagNo || "",
      barcode: row.barcode || "",
      rfid: row.rfid || "",
      store: storeId,
      vendorName: row.vendorName || "",
      purchaseDate: purchaseDate === "Invalid date" ? "" : purchaseDate,
      cost: row.cost ?? "",
      quantity: String(row.quantity ?? 1),
    });
    setErrorMessages({});
    setShowForm(true);
  };

  const requiredStar = <span style={{ color: "red" }}>*</span>;
  const units = jewellery?.inventory || [];

  const handleSubmit = () => {
    if (!formData.tagNo.trim()) {
      return setErrorMessages({ tagNo: "Tag No is required" });
    }

    if (formData.quantity === "" || Number(formData.quantity) < 1) {
      return setErrorMessages({ quantity: "Quantity must be 1 or more" });
    }

    const payload = {
      ...formData,
      jewellery: jewellery._id,
      tagNo: formData.tagNo.trim(),
      quantity: Number(formData.quantity),
    };

    if (editingId) {
      onEdit({ id: editingId, formData: payload });
      return;
    }

    onAdd(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6">{jewellery?.name || "N.A"}</Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Product Code: {jewellery?.productCode || "N.A"}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          {units.length === 0 && !showForm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingId(null);
                setFormData(initialForm);
                setErrorMessages({});
                setShowForm(true);
              }}
              sx={{
                bgcolor: "#752ADA",
                textTransform: "none",
                "&:hover": { bgcolor: "#5f1fb8" },
              }}
            >
              Add Inventory
            </Button>
          )}
          {showForm && (
            <Button
              variant="outlined"
              onClick={resetForm}
              sx={{
                textTransform: "none",
                color: "#752ADA",
                borderColor: "#752ADA",
              }}
            >
              Cancel
            </Button>
          )}
        </Box>

        {showForm && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
              {editingId ? "Edit Inventory" : "Add Inventory"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={<>Tag No {requiredStar}</>}
                  name="tagNo"
                  value={formData.tagNo}
                  onChange={handleChange}
                  error={!!errorMessages.tagNo}
                  helperText={errorMessages.tagNo}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Barcode"
                  name="barcode"
                  value={formData.barcode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="RFID"
                  name="rfid"
                  value={formData.rfid}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Store"
                  name="store"
                  value={formData.store}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store._id} value={store._id}>
                      {store.branchName}
                      {store.branchCode ? ` (${store.branchCode})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Vendor"
                  name="vendorName"
                  value={formData.vendorName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Purchase Date"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: moment().format("YYYY-MM-DD") }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="PurchaseCost"
                  name="cost"
                  value={formData.cost}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={<>Quantity {requiredStar}</>}
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  error={!!errorMessages.quantity}
                  helperText={errorMessages.quantity}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    min: 0,
                    maxLength: 6,
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                disabled={loading}
                onClick={handleSubmit}
                sx={{
                  bgcolor: "#752ADA",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#5f1fb8" },
                }}
              >
                {editingId ? "Update Inventory" : "Save Inventory"}
              </Button>
            </Box>
          </Box>
        )}

        <Typography variant="h6">Inventory Units</Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#b293c7ff" }}>
              <TableRow>
                <TableCell>Tag No</TableCell>
                <TableCell>Barcode</TableCell>
                <TableCell>RFID</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Purchase Date</TableCell>
                <TableCell>Purchase Cost</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Stock Status</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {units.length > 0 ? (
                units.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{row.tagNo || "N.A"}</TableCell>
                    <TableCell>{row.barcode || "N.A"}</TableCell>
                    <TableCell>{row.rfid || "N.A"}</TableCell>
                    <TableCell>
                      {row.store?.branchName
                        ? `${row.store.branchName}${row.store.branchCode ? ` (${row.store.branchCode})` : ""}`
                        : "N.A"}
                    </TableCell>
                    <TableCell>{row.vendorName || "N.A"}</TableCell>
                    <TableCell>
                      {row.purchaseDate
                        ? moment(row.purchaseDate).format("DD MMM YYYY")
                        : "N.A"}
                    </TableCell>
                    <TableCell>{row.cost ?? 0}</TableCell>
                    <TableCell>{row.quantity ?? 1}</TableCell>
                    <TableCell>{row.stockStatus || "N.A"}</TableCell>
                    <TableCell>
                      <button
                        disabled={loading}
                        style={{
                          backgroundColor:
                            row.isActive !== false ? "#43a047" : "#e53935",
                          color: "#fff",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        onClick={() => setToggleRow(row)}
                      >
                        {row.isActive !== false ? "Enable" : "Disable"}
                      </button>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={() => handleEdit(row)}
                        sx={{ color: "#752ADA" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No inventory units added yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DynamicDialog
        open={!!toggleRow}
        onClose={() => setToggleRow(null)}
        onSubmit={handleToggleSubmit}
        title="Change Inventory Status"
        message="Are you sure you want to change the status of this inventory unit?"
        cancelText="Cancel"
        submitText="Submit"
      />
    </Dialog>
  );
};

export default JewelleryInventory;
